import {useNavigate} from 'react-router-dom';
import {trpc, RouterOutput} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {Badge} from '@common-web/components/ui/badge';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronRight,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import {formatDistanceToNow, format} from 'date-fns';

type JobRun = RouterOutput['internal']['jobRun']['list']['jobRuns'][number];

type JobRunFilters = {jobName: string; status: string};

export function JobRunsList() {
  const navigate = useNavigate();

  const table = useDataTable<JobRunFilters>({
    defaultPageSize: 20,
    defaultFilters: {jobName: '', status: ''},
  });

  const {data: jobNames} = trpc.internal.jobRun.getJobNames.useQuery();

  const {
    data: jobRunsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = trpc.internal.jobRun.list.useQuery({
    page: table.page,
    pageSize: table.pageSize,
    jobName: table.filterValues.jobName || undefined,
    status: (table.filterValues.status || undefined) as
      | 'PENDING'
      | 'RUNNING'
      | 'COMPLETED'
      | 'FAILED'
      | undefined,
  });

  const {data: stats, isLoading: statsLoading} = trpc.internal.jobRun.getStats.useQuery({days: 7});

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return (
          <Badge className='bg-emerald text-white border-0 font-medium'>
            <CheckCircle2 className='h-3 w-3 mr-1' />
            Completed
          </Badge>
        );
      case 'FAILED':
        return (
          <Badge className='bg-red-500 text-white border-0 font-medium'>
            <XCircle className='h-3 w-3 mr-1' />
            Failed
          </Badge>
        );
      case 'RUNNING':
        return (
          <Badge className='bg-brand-blue text-white border-0 font-medium'>
            <Loader2 className='h-3 w-3 mr-1 animate-spin' />
            Running
          </Badge>
        );
      case 'PENDING':
        return (
          <Badge className='bg-brand-gray text-white border-0 font-medium'>
            <Clock className='h-3 w-3 mr-1' />
            Pending
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const columns: Column<JobRun>[] = [
    {
      id: 'jobName',
      header: 'Job',
      render: (run) => <div className='font-medium text-brand-navy'>{run.jobName}</div>,
    },
    {
      id: 'status',
      header: 'Status',
      render: (run) => getStatusBadge(run.status),
    },
    {
      id: 'triggeredBy',
      header: 'Trigger',
      render: (run) => (
        <span className='text-sm text-brand-gray capitalize'>{run.triggeredBy || 'manual'}</span>
      ),
    },
    {
      id: 'startedAt',
      header: 'Started',
      render: (run) => (
        <div className='text-sm'>
          <div className='text-brand-navy'>{format(new Date(run.startedAt), 'MMM d, h:mm a')}</div>
          <div className='text-brand-gray text-xs'>
            {formatDistanceToNow(new Date(run.startedAt), {addSuffix: true})}
          </div>
        </div>
      ),
    },
    {
      id: 'duration',
      header: 'Duration',
      render: (run) => (
        <span className='text-sm font-mono text-brand-navy'>{formatDuration(run.durationMs)}</span>
      ),
    },
    {
      id: 'errors',
      header: 'Errors',
      render: (run) =>
        run.errorCount > 0 ? (
          <Badge className='bg-amber/10 text-amber border-amber/20 font-medium'>
            <AlertTriangle className='h-3 w-3 mr-1' />
            {run.errorCount}
          </Badge>
        ) : (
          <span className='text-brand-gray'>-</span>
        ),
    },
    {
      id: 'actions',
      header: '',
      render: (run) => (
        <Button
          size='sm'
          variant='ghost'
          className='text-brand-blue hover:text-brand-blue-dark'
          onClick={() => navigate(`/jobs/${run.id}`)}
        >
          View
          <ChevronRight className='h-4 w-4 ml-1' />
        </Button>
      ),
    },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title='Job Runs'
          description='Monitor background job execution and view logs'
          icon={<Play className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          <PageHeaderAction tone='secondary' onClick={() => refetch()}>
            <RefreshCw className='h-4 w-4 mr-2' />
            Refresh
          </PageHeaderAction>
        </PageHeader>

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          {/* Stats Cards */}
          <div className='grid grid-cols-1 md:grid-cols-5 gap-4 mb-6'>
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray'>Total (7d)</CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-brand-navy'>{stats?.total || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <CheckCircle2 className='h-4 w-4 text-emerald' />
                  Completed
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-emerald'>{stats?.completed || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <XCircle className='h-4 w-4 text-red-500' />
                  Failed
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-red-500'>{stats?.failed || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray flex items-center gap-2'>
                  <Loader2 className='h-4 w-4 text-brand-blue' />
                  Running
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-brand-blue'>{stats?.running || 0}</div>
                )}
              </CardContent>
            </Card>

            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-brand-gray'>Success Rate</CardTitle>
              </CardHeader>
              <CardContent>
                {statsLoading ? (
                  <Skeleton className='h-8 w-12' />
                ) : (
                  <div className='text-3xl font-bold text-brand-navy'>
                    {stats?.successRate || 0}%
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Job Runs Table */}
          <DataTable<JobRun, JobRunFilters>
            table={table}
            columns={columns}
            data={jobRunsData?.jobRuns}
            total={jobRunsData?.totalCount}
            totalPages={jobRunsData?.totalPages}
            loading={isLoading}
            error={error}
            emptyMessage='No job runs found'
            title='Recent Runs'
            entity={{singular: 'run', plural: 'runs'}}
            filters={[
              {
                key: 'jobName',
                label: 'Jobs',
                type: 'select',
                placeholder: 'All Jobs',
                options: (jobNames ?? []).map((name) => ({value: name, label: name})),
              },
              {
                key: 'status',
                label: 'Status',
                type: 'select',
                placeholder: 'All Status',
                options: [
                  {value: 'COMPLETED', label: 'Completed'},
                  {value: 'FAILED', label: 'Failed'},
                  {value: 'RUNNING', label: 'Running'},
                  {value: 'PENDING', label: 'Pending'},
                ],
              },
            ]}
            rowKey={(run) => run.id}
          />
        </div>
      </div>
    </div>
  );
}
